import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';

import logoMarkPng from '../assets/logo_text.png';
import logoMarkAvif from '../assets/optimized/logo_text.avif';
import logoMarkWebp from '../assets/optimized/logo_text.webp';
import { type NavLink as NavLinkType } from '../lib/navigation';
import { SUPPORTED_LOCALES, useTranslations, type Locale } from '../i18n';

type HeaderProps = {
  links: NavLinkType[];
};

type LanguageOption = {
  code: Locale;
  short: string;
  label: string;
};

export function Header({ links }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, locale, setLocale, dictionary } = useTranslations();
  const logoRef = useRef<HTMLImageElement | null>(null);
  const localeLabels = useMemo(() => (dictionary.locales as Record<string, string>) ?? {}, [dictionary]);
  const languages = useMemo<LanguageOption[]>(
    () =>
      SUPPORTED_LOCALES.map((code) => ({
        code,
        short: code.toUpperCase(),
        label: localeLabels[code] ?? code.toUpperCase(),
      })),
    [localeLabels],
  );

  const closeMenu = () => setIsMenuOpen(false);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleLocaleSelect = (code: Locale) => {
    setLocale(code);
  };
  const handleLocaleSelectAndClose = (code: Locale) => {
    handleLocaleSelect(code);
    closeMenu();
  };

  useEffect(() => {
    logoRef.current?.setAttribute('fetchpriority', 'high');
  }, []);

  const menuLabel = t('header.menuLabel', 'Menu') as string;
  const menuOpen = t('header.menuOpen', 'Open navigation') as string;
  const menuClose = t('header.menuClose', 'Close navigation') as string;
  const languageToggleAria = t('header.aria.languageToggle', 'Switch language') as string;

  return (
    <header className="sticky top-0 z-50 border-b border-black/40 bg-black/90">
      <div className="mx-auto max-w-7xl px-6 py-5">
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-6">
          <NavLink
            to="/"
            onClick={() => {
              scrollToTop();
            }}
            className="flex shrink-0 items-center gap-3 overflow-visible focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c2673a]"
          >
            <picture>
              <source srcSet={logoMarkAvif} type="image/avif" />
              <source srcSet={logoMarkWebp} type="image/webp" />
              <img
                src={logoMarkPng}
                alt={t('header.brand', 'EverBranch')}
                className="h-10 w-auto origin-left scale-[2.4] md:h-12 md:scale-[2.9]"
                loading="eager"
                decoding="async"
                ref={logoRef}
              />
            </picture>
          </NavLink>
          <nav className="hidden items-center justify-center gap-6 text-sm font-semibold lg:flex lg:justify-self-center">
            {links.map((link) => {
              const label = (t(link.nameKey, link.label) as string) ?? link.label;
              const isExternal = link.path.startsWith('http');
              const isLunchMenu = link.nameKey === 'navigation.lunchMenu';
              const baseClass =
                'relative whitespace-nowrap px-2 pb-2 text-[0.82rem] tracking-[0.16em] transition-colors duration-200 after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-[var(--color-accent)] after:content-[""] after:transition-transform after:duration-200';
              const colorClass = isLunchMenu
                ? 'text-[var(--color-accent-bright)] hover:text-[var(--color-accent-bright)]'
                : 'text-white hover:text-white';

              if (isExternal) {
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={[baseClass, colorClass, 'hover:after:scale-x-100'].join(' ')}
                  >
                    {label}
                  </a>
                );
              }

              return (
                <NavLink
                  key={link.path}
                  to={link.path}
                  onClick={scrollToTop}
                  className={({ isActive }) =>
                    [
                      baseClass,
                      colorClass,
                      'hover:after:scale-x-100',
                      isActive ? 'after:scale-x-100' : '',
                    ].join(' ')
                  }
                >
                  {label}
                </NavLink>
              );
            })}
          </nav>
          <div className="flex shrink-0 items-center gap-3 justify-self-end text-white">
            <LanguageSwitcher
              languages={languages}
              active={locale}
              onSelect={handleLocaleSelect}
              ariaLabel={languageToggleAria}
              className="hidden sm:flex"
            />
            <button
              type="button"
              className="inline-flex h-12 w-12 items-center justify-center p-0 text-white transition hover:text-[var(--color-accent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-black lg:hidden"
              aria-expanded={isMenuOpen}
              aria-label={isMenuOpen ? menuClose : menuOpen}
              onClick={() => setIsMenuOpen((prev) => !prev)}
            >
              {isMenuOpen ? (
                <svg
                  aria-hidden="true"
                  focusable="false"
                  className="h-7 w-7 text-[var(--color-text-primary)]"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                >
                  <path d="M6 6l12 12" />
                  <path d="M18 6l-12 12" />
                </svg>
              ) : (
                <svg
                  aria-hidden="true"
                  focusable="false"
                  className="h-7 w-7 text-[var(--color-text-primary)]"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                >
                  <path d="M4 7h16" />
                  <path d="M4 12h16" />
                  <path d="M4 17h16" />
                </svg>
              )}
              <span className="sr-only">{menuLabel}</span>
            </button>
          </div>
        </div>
        {isMenuOpen && (
          <div
            className="fixed inset-x-0 bottom-0 top-[76px] z-40 bg-[rgba(7,10,14,0.96)] px-6 pb-10 pt-8 text-white backdrop-blur-sm lg:hidden"
            onClick={closeMenu}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="mx-auto flex h-full max-w-sm flex-col gap-8"
              onClick={(event) => event.stopPropagation()}
            >
              <nav className="flex flex-col gap-1 text-sm font-semibold uppercase tracking-[0.24em]">
                {links.map((link) => {
                  const label = (t(link.nameKey, link.label) as string) ?? link.label;
                  const isExternal = link.path.startsWith('http');
                  const isLunchMenu = link.nameKey === 'navigation.lunchMenu';

                  if (isExternal) {
                    return (
                      <a
                        key={link.path}
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          closeMenu();
                        }}
                        className={[
                          'flex items-center justify-between border-b border-white/15 px-2 py-3 transition hover:text-white',
                          isLunchMenu ? 'text-[var(--color-accent-bright)]' : 'text-white/80',
                        ].join(' ')}
                      >
                        {label}
                        <span aria-hidden="true" className="text-xs text-white/50">
                          ↗
                        </span>
                      </a>
                    );
                  }

                  return (
                    <NavLink
                      key={link.path}
                      to={link.path}
                      onClick={() => {
                        scrollToTop();
                        closeMenu();
                      }}
                      className={({ isActive }) =>
                        [
                          'flex items-center justify-between border-b border-white/15 px-2 py-3 transition',
                          isLunchMenu
                            ? 'text-[var(--color-accent-bright)]'
                            : isActive
                              ? 'bg-white/10 text-white'
                              : 'text-white/80 hover:text-white',
                        ].join(' ')
                      }
                    >
                      {label}
                    </NavLink>
                  );
                })}
              </nav>
              <LanguageSwitcher
                languages={languages}
                active={locale}
                onSelect={handleLocaleSelectAndClose}
                ariaLabel={languageToggleAria}
                className="w-full justify-between"
              />
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

type LanguageSwitcherProps = {
  languages: LanguageOption[];
  active: Locale;
  onSelect: (code: Locale) => void;
  ariaLabel: string;
  className?: string;
};

function LanguageSwitcher({ languages, active, onSelect, ariaLabel, className }: LanguageSwitcherProps) {
  const containerClass = [
    'flex items-center gap-1 rounded-full border border-white/15 bg-[rgba(11,14,18,0.68)] p-1 text-white shadow-[0_18px_48px_-30px_rgba(0,0,0,0.75)] transition',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClass} role="group" aria-label={ariaLabel}>
      {languages.map((language) => {
        const isActive = language.code === active;
        const buttonClass = [
          'flex items-center rounded-full px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.28em] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-black',
          isActive
            ? 'bg-[var(--color-accent)] text-white shadow-[0_14px_36px_-18px_rgba(194,103,58,0.6)]'
            : 'text-white/70 hover:text-white/100',
        ].join(' ');

        return (
          <button
            key={language.code}
            type="button"
            className={buttonClass}
            onClick={() => onSelect(language.code)}
            aria-pressed={isActive}
          >
            <span>{language.short}</span>
          </button>
        );
      })}
    </div>
  );
}
